import React, { useState } from "react";
import { useTranslation } from "next-i18next";
import handleDeepLink from "@/utils/handleDeepLink";
import clientDeepLinks from "@/utils/clientDeepLinks";
import { helpCenterLocaleHost } from "@/utils/util";
import { useRouter } from "next/router";

import Button from "@/components/Button";
import NoticeModalBottom from "@/containers/common/NoticeModalBottom";
import { FeeIcon, SecureIcon } from "@/components/RFIcon";
import useInApp from "@/hooks/useIsApp";
import BrowserCustomer from "@/containers/common/BrowserCustomer";
import clsx from "clsx";
import bridge from "@/utils/dsBridge";
import { BridgeCallHandlers } from "@/interfaces/enums";

interface SurveyFooterProps {
	loading?: boolean;
	nextBtn?: boolean;
	nextBtnText?: string;
	onNextBtnClick?: () => void;
	disabled?: boolean;
	showSecure?: boolean;
	showOnfido?: boolean;
	space?: boolean;
	onPrivacyClick?: () => void;
	onSecureClick?: () => void;
	onOnfidoClick?: () => void;
	extraContent?: React.ReactNode;
}

enum ModalUIState {
	PRIVACY,
	SECURE,
	ONFIDO,
}

function SurveyFooter({
	loading,
	onNextBtnClick,
	onPrivacyClick,
	onSecureClick,
	disabled = false,
	showSecure = true,
	nextBtn = true,
	space = false,
	extraContent,
	nextBtnText,
}: SurveyFooterProps) {
	const router = useRouter();
	const isInApp = useInApp();
	const { t } = useTranslation("survey");
	const [visible, setVisible] = useState(false);
	const [modalUI, setModalUi] = useState<ModalUIState>();
	const handleButtonClick = () => {
		if (disabled || loading) return;
		onNextBtnClick && onNextBtnClick();

		bridge.call(BridgeCallHandlers.ON_INVOKE_HAPTIC_ENGINE, {
			key: "style",
			value: "rigid",
		});
	};

	const showModal = (_state: ModalUIState) => {
		setModalUi(_state);
		setVisible(true);
	};

	const onfidoLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		e.stopPropagation();
		const url = "https://onfido.com/termsofuse";
		if (isInApp) {
			handleDeepLink(clientDeepLinks.webview(url));
			return;
		}
		window.location.href = url;
	};

	const onMoreInfoLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		e.stopPropagation();
		const url = `${helpCenterLocaleHost(router.locale)}/docs/legal/more-questions/`;
		if (isInApp) {
			handleDeepLink(clientDeepLinks.webview(url));
			return;
		}
		window.location.href = url;
	};

	const renderModalTitle = () => {
		switch (modalUI) {
			case ModalUIState.SECURE:
				return t("secure");
			case ModalUIState.PRIVACY:
				return t("privacy");
			case ModalUIState.ONFIDO:
				return t("onfido");
			default:
				return "";
		}
	};
	const renderModalBodyContent = () => {
		if (modalUI === ModalUIState.SECURE) {
			return t("PRIVACY_BY_REGULATION");
		} else if (modalUI === ModalUIState.PRIVACY) {
			return (
				<>
					{t("SECURE_YOUR_MONEY_IS_SAFE")}{" "}
					<a className="link-text underline underline-offset-2 cursor-pointer" onClick={onMoreInfoLinkClick}>
						{t("more-info-about-rockflow")}
					</a>
				</>
			);
		} else if (modalUI === ModalUIState.ONFIDO) {
			const descriptions = t("ONFIDO_WE_ARE", { seg: "RF_SEG" }).split("RF_SEG");
			return (
				<>
					{descriptions[0]}
					<a className="link-text underline underline-offset-2 cursor-pointer" onClick={onfidoLinkClick}>
						https://onfido.com/termsofuse
					</a>
					{descriptions[1]}
				</>
			);
		}
	};

	return (
		<div className={clsx("flex-shrink-0 pt-4 pb-6", space ? "px-4.5" : "")}>
			<div className="flex items-center justify-between space-x-4">
				<div className="flex-1 flex items-center gap-2">
					<Button
						type="secondary"
						size="sm"
						leading={<FeeIcon className="w-[1.125rem] h-[1.125rem] mr-1" />}
						onClick={() => {
							showModal(ModalUIState.PRIVACY);
							onPrivacyClick && onPrivacyClick();
						}}
					>
						{t("privacy")}
					</Button>

					{showSecure && (
						<Button
							type="secondary"
							size="sm"
							leading={<SecureIcon className="w-3.5 h-4 mr-1" />}
							onClick={() => {
								showModal(ModalUIState.SECURE);
								onSecureClick && onSecureClick();
							}}
						>
							{t("secure")}
						</Button>
					)}
				</div>
				{!isInApp && <BrowserCustomer />}
			</div>

			<div className="flex flex-col md:gap-x-4 mt-5 gap-y-2.5 md:flex-row-reverse">
				{nextBtn && (
					<Button
						onClick={handleButtonClick}
						loading={loading}
						disabled={disabled}
						block
						className={clsx(extraContent ? "md:w-1/2" : "")}
					>
						{nextBtnText || t("continue", { ns: "common" })}
					</Button>
				)}
				{extraContent}
			</div>

			<NoticeModalBottom
				visible={visible}
				onClose={() => setVisible(false)}
				title={renderModalTitle()}
				content={renderModalBodyContent()}
			/>
		</div>
	);
}

export default SurveyFooter;
